import { ChangeContext, LabelType, Options } from '@angular-slider/ngx-slider';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
    CmpsDialogRef,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { debounceTime, skip, startWith, tap } from 'rxjs/operators';

import { AuthService } from '../../../../auth/auth.service';
import { NoteCategory } from '../../../../coaching-base/general';
import { CoachingSwitchboardService } from '../../../../coaching-base/switchboard';
import { getDialogItems } from '../../../../coaching-commitment/functions/get-dialog-items.function';
import { goToDraftOrCommitment } from '../../../../coaching-commitment/functions/go-to-draft-or-commitment.function';
import { prepStatuses } from '../../../../coaching-commitment/functions/prep-statuses.function';
import { TabRefId } from '../../../../coaching-detail';
import { NoteDetailFormValue } from '../../../../coaching-note/note-detail-form-value.model';
import { noteDetailFormValueReverseTransformer } from '../../../../coaching-note/note-detail.form';
import { CoachingObservationDialogService } from '../../../../coaching-observation/coaching-observation-dialog.service';
import { NavigationScenario } from '../../../../coaching-observation/observation/observation.model';
import { FocusShallow } from '../../../../coaching-tags/tags/tags.model';
import { CmpsDatePipe } from '../../../../common/date.pipe';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../../../common/take-until-destroy.decorator';
import {
    TranscriptEntry,
    TranscriptParticipant,
} from '../../../../platform-call';
import { useDispatcher } from '../../../../store.decorator';
import { useSelector, useSelector2 } from '../../../store-use-selector';
import { copyTextToClipboard } from '../../function/copy-text-to-clipboard.function';
import { createNoteFromQuotes } from '../../function/create-note-from-quotes.function';
import { createNote } from '../../function/create-note.function';
import { createObservationOverride } from '../../function/create-observation-override.function';
import { BehaviourTag } from '../../model/behaviour-tag.model';
import { addCommitmentNotes } from '../../state/add-commitment-notes.action';
import { deleteTranscriptQuotes } from '../../state/delete-transcript-quotes.action';
import { filterQuotes } from '../../state/filter-quotes.action';
import { resetNav } from '../../state/reset-nav.action';
import { reset } from '../../state/reset.action';
import { selectQuotes } from '../../state/select-quotes.action';
import { setNoteViaSummary } from '../../state/set-note-via-summary.action';
import { updateTranscript } from '../../state/update-transcript.action';
import { TranscriptDialogService } from '../../transcript-dialog.service';
import { TranscriptDialogState } from '../../transcript-dialog.state';

import { TranslationKeyMap } from './notes-page.i18n';

@Component({
    selector: 'cmps-transcript-dialog-notes-page',
    templateUrl: './notes-page.component.html',
    host: {
        class: 'cmps-transcript-dialog-notes-page',
    },
})
@TakeUntilDestroy()
export class NotesPageComponent implements OnInit, OnDestroy {
    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _NoteCategory = NoteCategory;

    /** @private */ _descVisible: boolean;
    /** @private */ _descExpanded: boolean;
    /** @private */ _formHasCommitmentNotes: boolean;
    /** @private */ _formHasObservationNotes: boolean;
    /** @private */ _formHasNotes: boolean;
    /** @private */ _form: FormGroup;
    /** @private */ _validFocusWarning: boolean;

    private _keywordSubj: Subject<string>;
    private _keywordSub: Subscription;

    private _sliderSubj: Subject<{ from: number; to: number }>;
    private _sliderSub: Subscription;
    /** @private */ _sliderValue: number;
    /** @private */ _sliderHighValue: number;
    /** @private */ _sliderOptions: Options;

    /** @private */ _summaryContent: string;

    constructor(
        private readonly _formBuilder: FormBuilder,
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _router: Router,
        private readonly _translateService: TranslateService,
        /** @private */ readonly _service: TranscriptDialogService,
        /** @private */ readonly _dialogRef: CmpsDialogRef<any, void>,
        private readonly _datePipe: CmpsDatePipe,
        private readonly _dialog: CmpsDialog,
        private readonly _switchboardService: CoachingSwitchboardService,
        private readonly _observationDialogService: CoachingObservationDialogService,
        private readonly _authService: AuthService,
    ) {
        this._sliderOptions = {
            floor: 0,
            ceil: 1,
            step: 1,
            hideLimitLabels: true,
            translate: (value: number, label: LabelType): string => {
                switch (label) {
                    case LabelType.Low:
                        return this._datePipe.transform(
                            this._service.state?.transcript?.entries?.[0]
                                ?.endTime,
                            'h:mm:ss a',
                        );
                    case LabelType.High:
                        return this._datePipe.transform(
                            this._service.state?.transcript?.entries?.at(-1)
                                ?.endTime,
                            'h:mm:ss a',
                        );
                }
            },
        };

        this._descExpanded = true;

        this._form = this._formBuilder.group({
            commitmentNotes: [[]],
            observationNotes: [[]],
        });
    }

    ngOnInit(): void {
        this._keywordSubj = new Subject();
        this._keywordSub = this._keywordSubj
            .asObservable()
            .pipe(
                debounceTime(500),
                tap((keyword) => {
                    useDispatcher(
                        this._service,
                        filterQuotes({
                            opts: {
                                ...this._service.state.filterOpts,
                                keyword,
                            },
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._sliderSubj = new Subject();
        useSelector<{}>('coaching.transcriptDialog.sliderOptions', () => null)
            .pipe(
                tap(() => {
                    this._sliderOptions = {
                        ...this._sliderOptions,
                        floor: this._service.state?.sliderOptions?.floorNum,
                        ceil: this._service.state?.sliderOptions?.ceilNum,
                    };
                    // value/highValue only seem to reset when using two-way data-binding
                    this._sliderValue =
                        this._service.state?.sliderOptions?.floorNum;
                    this._sliderHighValue =
                        this._service.state?.sliderOptions?.ceilNum;

                    if (this._sliderSub != null) {
                        return;
                    }
                    this._sliderSub = this._sliderSubj
                        .asObservable()
                        .pipe(
                            // skip(2), // skip initial change events for floor and ceil
                            debounceTime(500),
                            tap((timeRange) => {
                                useDispatcher(
                                    this._service,
                                    filterQuotes({
                                        opts: {
                                            ...this._service.state.filterOpts,
                                            timeRange,
                                        },
                                    }),
                                );
                            }),
                            takeUntilDestroy(this),
                        )
                        .subscribe();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useSelector2<BehaviourTag>(
            (state) =>
                (state?.coaching?.transcriptDialog as TranscriptDialogState)
                    ?.cmtNotesFocus,
        )
            .pipe(
                skip(1),
                tap(([prevState, state]) => {
                    if (prevState !== state) {
                        this._validFocus();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._bindFormHasNotesCheck();

        this._bindWillNavigateCheck();
    }

    ngOnDestroy(): void {
        this._keywordSub?.unsubscribe();
        this._keywordSubj?.complete();

        this._sliderSub?.unsubscribe();
        this._sliderSubj?.complete();
    }

    /** @private */
    _onResetClick(): void {
        useDispatcher(this._service, reset({}));
    }

    /** @private */
    _onSelectAllQuotesClick(): void {
        useDispatcher(
            this._service,
            selectQuotes({
                quotes: this._service.state.filteredQuotes,
                mode: 'toggle-all',
            }),
        );
    }

    /** @private */
    _onQuoteCheckboxChange(value: TranscriptEntry): void {
        useDispatcher(this._service, selectQuotes({ quotes: [value] }));
    }

    /** @private */
    _onDeleteQuotesClick(): void {
        const transcriptQuoteIds = this._service.state.selectedQuotes.map(
            (quote) => quote.transcriptEntryId,
        );
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._11038_DO_YOU_WANT_TO_PERMANENTLY_DELETE_THESE_LINES,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret === 0) {
                        useDispatcher(
                            this._service,
                            deleteTranscriptQuotes({
                                transcriptQuoteIds,
                            }),
                        );
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onCopyToClipboardClick(): void {
        const text = (this._service.state?.selectedQuotes ?? []).reduce(
            (p, c) => {
                return `${p}${c.text ?? ''}\r\n`;
            },
            '',
        );
        copyTextToClipboard(text);
    }

    /** @private */
    _onCreateCommitmentNoteClick(): void {
        const note = createNoteFromQuotes(
            this._service.state?.selectedQuotes,
            this._authService.user,
        );
        this._form.patchValue({
            commitmentNotes: [
                ...(this._form.value.commitmentNotes ?? []),
                note,
            ],
        });

        useDispatcher(this._service, selectQuotes({ quotes: [], mode: 'set' }));
    }

    /** @private */
    _onCreateObservationNoteClick(): void {
        const note = createNoteFromQuotes(
            this._service.state?.selectedQuotes,
            this._authService.user,
        );
        this._form.patchValue({
            observationNotes: [
                ...(this._form.value.observationNotes ?? []),
                note,
            ],
        });

        useDispatcher(this._service, selectQuotes({ quotes: [], mode: 'set' }));
    }

    /** @private */
    _onRemoveAllNotesClick(): void {
        useDispatcher(
            this._service,
            setNoteViaSummary({
                commitment: false,
                observation: false,
            }),
        );
        this._form.patchValue({ commitmentNotes: [], observationNotes: [] });
    }

    /** @private */
    _onOpenNewObservationClick(): void {
        const transcriptFocus =
            this._service.state.transcript?.focuses?.find(
                (e) => e.isPrimary === true,
            ) ??
            this._service.state.transcript?.focuses?.find(
                (e) => e.isPrimary !== true,
            );
        const focus: FocusShallow = {
            behaviourId: transcriptFocus?.behaviourId,
            subBehaviourId: transcriptFocus?.subBehaviourId,
        };

        const notes = this._addSummaryNote(
            'observation',
            this._form.value.observationNotes,
        );
        const observation = createObservationOverride(
            this._service.state?.transcript,
            notes,
        );

        let scenario: NavigationScenario;
        if (!this._switchboardService.commitment.connected()) {
            scenario =
                focus.behaviourId != null
                    ? NavigationScenario.POP_UP__FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_NO_COMMITMENT_LOADED
                    : NavigationScenario.POP_UP__NO_FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_NO_COMMITMENT_LOADED;
        } else {
            scenario =
                focus.behaviourId != null
                    ? NavigationScenario.POP_UP__FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_HAS_COMMITMENT_LOADED
                    : NavigationScenario.POP_UP__NO_FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_HAS_COMMITMENT_LOADED;
        }

        let doNotNavigate: boolean = null;
        this._observationDialogService.openNew({
            focus,
            extraOptions: {
                observation,
                scenario,
                canChangeActiveFocus: true,
                canDeactivateWithoutConfirm: true,
                willPublish: (opts: { doNotNavigate: boolean }) => {
                    useDispatcher(
                        this._service,
                        updateTranscript({
                            transcript: {
                                ...this._service.state.transcript,
                                caSummary: {
                                    ...this._service.state.transcript.caSummary,
                                    content: this._summaryContent,
                                },
                            },
                        }),
                    );

                    doNotNavigate = opts.doNotNavigate;
                },
                doNavigate: () => doNotNavigate,
                onNavigate: () => {
                    if (!doNotNavigate) {
                        this._switchboardService.observationReview.sbWillNavigate();
                        this._switchboardService.commitment.sbWillNavigate();
                        return;
                    }

                    this._onResetClick();

                    // ideally this should be wrapped in a useSelector()
                    this._onRemoveAllNotesClick();
                },
            },
        });
    }

    /** @private */
    _onPublishClick(): void {
        if (!this._validFocus()) {
            return;
        }
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._11033_WOULD_YOU_LIKE_TO_PUBLISH_THESE_COMMITMENT_NOTES_AND_CONTINUE,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._11039_PUBLISH_AND_CONTINUE,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._11040_PUBLISH_AND_CLOSE,
                    ),
                    data: 1,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._121_CANCEL,
                    ),
                    data: 2,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret === 2) {
                        return;
                    }
                    const notes = this._addSummaryNote(
                        'commitment',
                        this._form.value.commitmentNotes,
                    ).map(noteDetailFormValueReverseTransformer);
                    useDispatcher(
                        this._service,
                        addCommitmentNotes({
                            performerId:
                                this._service.state.transcript.attendees?.[0]
                                    ?.userId,
                            coachId:
                                this._service.state.transcript.organizer
                                    ?.userId,
                            notes,
                            willNavigate: ret === 1,
                        }),
                    );
                    useDispatcher(
                        this._service,
                        updateTranscript({
                            transcript: {
                                ...this._service.state.transcript,
                                caSummary: {
                                    ...this._service.state.transcript.caSummary,
                                    content: this._summaryContent,
                                },
                            },
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onDescriptionChange(value: string): void {
        if (this._service.state?.transcript.description === value) {
            return;
        }
        useDispatcher(
            this._service,
            updateTranscript({
                transcript: {
                    ...this._service.state.transcript,
                    description: value,
                },
            }),
        );
    }

    /** @private */
    _onFilterParticipantClick(participant: TranscriptParticipant | null): void {
        // TODO revise. not sure if this is to be toggleable
        // let participants: CallParticipant[] = [];
        // if (participant != null) {
        //     participants = this.state.filterOpts.participants ?? [];
        //     const index = participants.indexOf(participant);
        //     if (index === -1) {
        //         participants.push(participant);
        //     } else {
        //         participants.splice(index, 1);
        //     }
        // }

        const participants = participant == null ? null : [participant];

        useDispatcher(
            this._service,
            filterQuotes({
                opts: {
                    ...this._service.state.filterOpts,
                    participants,
                },
            }),
        );
    }

    /** @private */
    _onKeywordFilterChange(value: string): void {
        this._keywordSubj.next(value);
    }

    /** @private */
    _onSliderValueChange(value: ChangeContext): void {
        this._sliderSubj.next({ from: value.value, to: value.highValue });
    }

    private _bindFormHasNotesCheck(): void {
        const check = (value: any): void => {
            this._formHasCommitmentNotes =
                this._service.state?.didAddCommitmentNoteViaSummary === true ||
                value?.commitmentNotes?.length > 0;
            this._formHasObservationNotes =
                this._service.state?.didAddObservationNoteViaSummary === true ||
                value?.observationNotes?.length > 0;
            this._formHasNotes =
                this._formHasCommitmentNotes || this._formHasObservationNotes;
        };

        combineLatest([
            useSelector2<boolean>(
                (state) =>
                    (state?.coaching?.transcriptDialog as TranscriptDialogState)
                        ?.didAddCommitmentNoteViaSummary,
            ),
            useSelector2<boolean>(
                (state) =>
                    (state?.coaching?.transcriptDialog as TranscriptDialogState)
                        ?.didAddObservationNoteViaSummary,
            ),
            this._form.valueChanges.pipe(startWith(null)),
        ])
            .pipe(
                tap((next) => {
                    check(next[2]);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        check(this._form.value);
    }

    private _bindWillNavigateCheck(): void {
        const check = (): void => {
            if (
                this._service.state.willNavigate === false &&
                (this._service.state.navigateCmtId != null ||
                    this._service.state.navigateDraftId != null)
            ) {
                this._onRemoveAllNotesClick();
                this._switchboardService.commitment.sbReload(
                    this._service.state.navigateCmtId,
                    this._service.state.navigateDraftId,
                );
                void this._switchboardService.pillarTwo.sbReload(
                    TabRefId.COMMITMENTS,
                );
                useDispatcher(this._service, resetNav({}));
            }
            if (this._service.state.willNavigate === true) {
                this._onRemoveAllNotesClick();
                this._switchboardService.commitment.sbWillNavigate();
                void this._switchboardService.pillarTwo.sbReload(
                    TabRefId.COMMITMENTS,
                );
                const dialogItems = getDialogItems(
                    this._form.value,
                    false,
                    this._service.state.behaviours,
                    this._service.state.subBehaviours,
                    this._switchboardService,
                );
                const { statuses, promiseStatuses } = prepStatuses(
                    false,
                    dialogItems,
                    this._switchboardService,
                    null,
                );
                goToDraftOrCommitment(
                    this._service.state.navigateDraftId,
                    this._service.state.navigateCmtId,
                    this._router,
                    this._activatedRoute,
                    this._switchboardService,
                    statuses,
                    promiseStatuses,
                );
                useDispatcher(this._service, resetNav({}));
                this._dialogRef.close();
            }
        };

        useSelector2<boolean>(
            (state) =>
                (state?.coaching?.transcriptDialog as TranscriptDialogState)
                    ?.willNavigate,
        )
            .pipe(
                skip(1),
                tap(() => {
                    check();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _validFocus(): boolean {
        const result = this._service.state?.cmtNotesFocus?.behaviour != null;
        this._validFocusWarning = !result;
        return result;
    }

    private _addSummaryNote(
        which: 'commitment' | 'observation',
        notes: NoteDetailFormValue[],
    ): NoteDetailFormValue[] {
        const nextNotes = [...notes];

        if (
            (which === 'commitment' &&
                this._service.state?.didAddCommitmentNoteViaSummary === true) ||
            (which === 'observation' &&
                this._service.state?.didAddObservationNoteViaSummary === true)
        ) {
            const note = createNote(
                this._summaryContent ??
                    this._service.state.transcript.caSummary.content,
                this._authService.user,
            );
            nextNotes.splice(0, 0, note);
        }

        return nextNotes;
    }
}
